import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useEventForm } from "./useEventForm";

export interface EventImages {
  bannerLists: string | File | null;
  bannerDetails: string | File | null;
  bannerHomePage: string | File | null;
  bannerInvoice: string | File | null;
}

interface EventImagesContextType {
  bannerLists: string | File | null;
  bannerDetails: string | File | null;
  bannerHomePage: string | File | null;
  bannerInvoice: string | File | null;
  handleBannerListsChange: (photo: string | File | null) => void;
  handleBannerDetailsChange: (photo: string | File | null) => void;
  handleBannerHomePageChange: (photo: string | File | null) => void;
  handleBannerInvoiceChange: (photo: string | File | null) => void;
  getImages: () => EventImages;
}

const EventImagesContext = createContext<EventImagesContextType | undefined>(undefined);

export function EventImagesProvider({ children }: { children: ReactNode }) {
  const { event, updateEventField } = useEventForm();

  const [bannerLists, setBannerLists] = useState<string | File | null>(null);
  const [bannerDetails, setBannerDetails] = useState<string | File | null>(null);
  const [bannerHomePage, setBannerHomePage] = useState<string | File | null>(null);
  const [bannerInvoice, setBannerInvoice] = useState<string | File | null>(null);

  useEffect(() => {
    if (event?.bannerLists) {
      setBannerLists(event.bannerLists);
    } else {
      setBannerLists(null);
    }
    if (event?.bannerDetails) {
      setBannerDetails(event.bannerDetails);
    } else {
      setBannerDetails(null);
    }
    if (event?.bannerHomePage) {
      setBannerHomePage(event.bannerHomePage);
    } else {
      setBannerHomePage(null);
    }
    if (event?.bannerInvoice) {
      setBannerInvoice(event.bannerInvoice);
    } else {
      setBannerInvoice(null);
    }
  }, [event?.bannerLists, event?.bannerDetails, event?.bannerHomePage, event?.bannerInvoice]);

  const handleBannerListsChange = useCallback((photo: string | File | null) => {
    setBannerLists(photo);
    if (typeof photo === "string") {
      updateEventField("bannerLists", photo);
    }
  }, [updateEventField]);

  const handleBannerDetailsChange = useCallback((photo: string | File | null) => {
    setBannerDetails(photo);
    if (typeof photo === "string") {
      updateEventField("bannerDetails", photo);
    }
  }, [updateEventField]);

  const handleBannerHomePageChange = useCallback((photo: string | File | null) => {
    setBannerHomePage(photo);
    if (typeof photo === "string") {
      updateEventField("bannerHomePage", photo);
    }
  }, [updateEventField]);

  const handleBannerInvoiceChange = useCallback((photo: string | File | null) => {
    setBannerInvoice(photo);
    if (typeof photo === "string") {
      updateEventField("bannerInvoice", photo);
    }
  }, [updateEventField]);

  const getImages = useCallback((): EventImages => {
    return {
      bannerLists,
      bannerDetails,
      bannerHomePage,
      bannerInvoice,
    };
  }, [bannerLists, bannerDetails, bannerHomePage, bannerInvoice]);

  const value: EventImagesContextType = {
    bannerLists,
    bannerDetails,
    bannerHomePage,
    bannerInvoice,
    handleBannerListsChange,
    handleBannerDetailsChange,
    handleBannerHomePageChange,
    handleBannerInvoiceChange,
    getImages,
  };

  return (
    <EventImagesContext.Provider value={value}>
      {children}
    </EventImagesContext.Provider>
  );
}

export function useEventImages() {
  const context = useContext(EventImagesContext);
  if (!context) {
    throw new Error("useEventImages must be used within EventImagesProvider");
  }
  return context;
}


